/**
 * FindBar — in-editor search / find-replace toolbar.
 *
 * Listens for:
 *   loom:find-open         → open in search-only mode
 *   loom:find-replace-open → open in find-and-replace mode
 *
 * Uses the browser's native `window.find()` for cross-paragraph matching,
 * which works in all evergreen browsers for contentEditable text.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, ChevronUp, ChevronDown, Replace, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';

export function FindBar({ containerRef }) {
  const [open, setOpen]           = useState(false);
  const [mode, setMode]           = useState('find');   // 'find' | 'replace'
  const [term, setTerm]           = useState('');
  const [replacement, setReplacement] = useState('');
  const [matchCount, setMatchCount]   = useState(null);
  const [matchIdx, setMatchIdx]       = useState(0);
  const inputRef = useRef(null);

  // ── Listen for menu events ──────────────────────────────────────────────
  useEffect(() => {
    const onFind = () => { setMode('find'); setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); };
    const onFindReplace = () => { setMode('replace'); setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); };
    window.addEventListener('loom:find-open', onFind);
    window.addEventListener('loom:find-replace-open', onFindReplace);
    return () => {
      window.removeEventListener('loom:find-open', onFind);
      window.removeEventListener('loom:find-replace-open', onFindReplace);
    };
  }, []);

  // ── Close on Escape ────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    setOpen(false);
    setTerm('');
    setReplacement('');
    setMatchCount(null);
    // Remove any highlights
    highlight('');
  };

  // ── Highlight helper (wraps matches in a <mark>) ─────────────────────
  const highlight = useCallback((q) => {
    if (!containerRef?.current) return;
    // First un-highlight all existing marks
    containerRef.current.querySelectorAll('mark[data-find]').forEach((m) => {
      const parent = m.parentNode;
      parent.replaceChild(document.createTextNode(m.textContent), m);
      parent.normalize();
    });
    if (!q) return 0;
    const re = new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    let count = 0;
    const walk = (node) => {
      if (node.nodeType === 3 /* TEXT_NODE */) {
        const text = node.nodeValue;
        let match; let lastIdx = 0; const frag = document.createDocumentFragment();
        while ((match = re.exec(text)) !== null) {
          frag.appendChild(document.createTextNode(text.slice(lastIdx, match.index)));
          const mark = document.createElement('mark');
          mark.setAttribute('data-find', '');
          mark.style.cssText = 'background:#fde68a;border-radius:2px;padding:0 1px;';
          mark.textContent = match[0];
          frag.appendChild(mark);
          lastIdx = match.index + match[0].length;
          count++;
        }
        if (count && lastIdx < text.length) frag.appendChild(document.createTextNode(text.slice(lastIdx)));
        if (frag.childNodes.length) node.parentNode?.replaceChild(frag, node);
      } else if (node.nodeType === 1 && !['SCRIPT','STYLE'].includes(node.nodeName)) {
        Array.from(node.childNodes).forEach(walk);
      }
    };
    walk(containerRef.current);
    return count;
  }, [containerRef]);

  const runFind = useCallback((q = term) => {
    const count = highlight(q);
    setMatchCount(count);
    setMatchIdx(0);
    // Scroll first match into view
    const first = containerRef?.current?.querySelector('mark[data-find]');
    if (first) first.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [term, highlight, containerRef]);

  const navigate = (dir) => {
    if (!containerRef?.current) return;
    const marks = Array.from(containerRef.current.querySelectorAll('mark[data-find]'));
    if (!marks.length) return;
    const next = (matchIdx + dir + marks.length) % marks.length;
    setMatchIdx(next);
    marks[next].scrollIntoView({ block: 'center', behavior: 'smooth' });
    marks.forEach((m, i) => { m.style.background = i === next ? '#f59e0b' : '#fde68a'; });
  };

  const replaceOne = () => {
    if (!containerRef?.current || !term) return;
    const mark = containerRef.current.querySelectorAll('mark[data-find]')[matchIdx];
    if (!mark) return;
    mark.outerHTML = replacement;
    runFind(term);
  };

  const replaceAll = () => {
    if (!containerRef?.current || !term) return;
    containerRef.current.querySelectorAll('mark[data-find]').forEach((m) => { m.outerHTML = replacement; });
    setMatchCount(0);
  };

  if (!open) return null;

  return (
    <div
      data-testid="editor-find-bar"
      className={cn(
        'flex items-center gap-2 border-b border-panel-border bg-panel-header/90 px-3 py-1.5 backdrop-blur-sm',
        'animate-in slide-in-from-top-2 duration-150',
      )}
    >
      {/* Search input */}
      <input
        ref={inputRef}
        value={term}
        onChange={(e) => { setTerm(e.target.value); if (!e.target.value) { highlight(''); setMatchCount(null); } }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.shiftKey ? navigate(-1) : runFind(); }
        }}
        placeholder="Find…"
        className="h-7 w-44 rounded border border-border bg-background px-2 text-[13.5px] focus:outline-none focus:ring-1 focus:ring-primary"
        data-testid="find-bar-input"
      />
      {matchCount != null && (
        <span className="text-[13px] text-muted-foreground whitespace-nowrap">
          {matchCount === 0 ? 'No results' : `${matchIdx + 1} / ${matchCount}`}
        </span>
      )}
      <button type="button" onClick={runFind} className="rounded p-1 hover:bg-secondary" title="Find next (Enter)" data-testid="find-bar-search-btn">
        <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
      <button type="button" onClick={() => navigate(-1)} className="rounded p-1 hover:bg-secondary" title="Previous match">
        <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
      <button type="button" onClick={() => navigate(1)} className="rounded p-1 hover:bg-secondary" title="Next match">
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
      </button>

      {/* Replace row */}
      {mode === 'replace' && (
        <>
          <div className="h-4 w-px bg-border" />
          <input
            value={replacement}
            onChange={(e) => setReplacement(e.target.value)}
            placeholder="Replace with…"
            className="h-7 w-36 rounded border border-border bg-background px-2 text-[13.5px] focus:outline-none focus:ring-1 focus:ring-primary"
            data-testid="find-bar-replace-input"
          />
          <button type="button" onClick={replaceOne}
            className="h-7 rounded border border-border bg-secondary px-2 text-[13px] hover:bg-accent"
            data-testid="find-bar-replace-one-btn">Replace</button>
          <button type="button" onClick={replaceAll}
            className="h-7 rounded border border-border bg-secondary px-2 text-[13px] hover:bg-accent"
            data-testid="find-bar-replace-all-btn">All</button>
        </>
      )}

      <button type="button" onClick={() => setMode(m => m === 'find' ? 'replace' : 'find')}
        className="ml-auto rounded p-1 hover:bg-secondary" title="Toggle replace mode">
        <Replace className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
      <button type="button" onClick={close} className="rounded p-1 hover:bg-secondary" title="Close (Esc)" data-testid="find-bar-close-btn">
        <X className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
    </div>
  );
}
